<template>
  <div class="mt-4">
    <div class="d-flex align-items-center justify-content-between mb-3">
      <span style="font-size:14px;font-weight:700;text-transform:uppercase;letter-spacing:0.4px;color:rgba(253,71,25,0.85);">
        <i class="bi bi-people text-primary mr-1"></i> Memberships
      </span>
      <a href="/subscriptions" class="text-primary" style="font-size:12px;font-weight:600;">
        Open subscriptions <i class="bi bi-chevron-right" style="font-size:10px;"></i>
      </a>
    </div>

    <div v-if="loading && !d" class="row" style="row-gap:20px;">
      <div v-for="n in 2" :key="n" class="col-12" :class="n === 1 ? 'col-xl-8' : 'col-xl-4'">
        <div class="bg-white border" :style="card" style="height:300px;"></div>
      </div>
    </div>

    <p v-else-if="error" class="text-danger" style="font-size:13px;">{{ error }}</p>

    <template v-else-if="d">
      <div class="row" style="row-gap:20px;">

        <!-- Health + income/check-ins chart -->
        <div class="col-12 col-xl-8">
          <div class="bg-white border h-100" :style="card">
            <div class="row mb-3" style="row-gap:18px;">
              <div v-for="s in healthStats" :key="s.label" class="col-6 col-md-4">
                <div class="text-secondary mb-1" style="font-size:12px;">
                  {{ s.label }}
                  <i v-if="s.help" class="bi bi-question-circle" style="font-size:11px;" :title="s.help"></i>
                </div>
                <span :class="s.danger ? 'text-danger' : 'text-dark'" style="font-size:18px;font-weight:700;letter-spacing:-0.2px;">{{ s.value }}</span>
              </div>
            </div>
            <div class="d-flex flex-wrap mb-2" style="gap:14px;">
              <span class="d-inline-flex align-items-center text-secondary" style="font-size:12px;gap:6px;">
                <span style="width:10px;height:10px;border-radius:3px;background:#fd4719;"></span> Membership income
              </span>
              <span class="d-inline-flex align-items-center text-secondary" style="font-size:12px;gap:6px;">
                <span style="width:14px;height:2px;background:#343a40;"></span> Check-ins
              </span>
            </div>
            <div style="position:relative;height:220px;">
              <canvas ref="combo"></canvas>
            </div>
          </div>
        </div>

        <!-- Members donut -->
        <div class="col-12 col-xl-4">
          <div class="bg-white border h-100" :style="card">
            <div class="mb-3" style="font-size:12px;font-weight:600;text-transform:uppercase;letter-spacing:0.4px;color:rgba(253,71,25,0.8);">Members now</div>
            <div class="d-flex align-items-center" style="gap:20px;">
              <div style="position:relative;width:150px;height:150px;flex-shrink:0;">
                <canvas ref="donut"></canvas>
                <div class="text-center" style="position:absolute;top:50%;left:50%;transform:translate(-50%,-50%);pointer-events:none;">
                  <div class="text-dark" style="font-size:26px;font-weight:700;line-height:1;">{{ d.members.active }}</div>
                  <div class="text-secondary" style="font-size:11px;">active</div>
                </div>
              </div>
              <div class="flex-grow-1">
                <div v-for="(seg, i) in donutSegments" :key="seg.label" class="d-flex justify-content-between align-items-center py-1" style="font-size:13px;">
                  <span class="d-inline-flex align-items-center text-secondary" style="gap:6px;">
                    <span :style="{ width: '10px', height: '10px', borderRadius: '3px', background: donutColors[i] }"></span>{{ seg.label }}
                  </span>
                  <strong class="text-dark">{{ seg.value }}</strong>
                </div>
                <div class="d-flex justify-content-between align-items-center py-1 border-top mt-1" style="font-size:13px;">
                  <span class="text-secondary">New this period</span>
                  <strong class="text-primary">+{{ d.members.new }}</strong>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Busiest times heatmap -->
        <div class="col-12 col-lg-7">
          <div class="bg-white border h-100" :style="card">
            <div class="d-flex align-items-center justify-content-between mb-3">
              <span style="font-size:12px;font-weight:600;text-transform:uppercase;letter-spacing:0.4px;color:rgba(253,71,25,0.8);">Busiest times <i class="bi bi-question-circle text-secondary" style="font-size:11px;" title="Check-ins by day and hour for the selected dates. Darker = busier."></i></span>
              <span v-if="peak" class="text-secondary" style="font-size:12px;">Peak: <strong class="text-dark">{{ peak }}</strong></span>
            </div>
            <div v-if="heatHours.length" style="overflow-x:auto;">
              <table class="mb-0" style="border-collapse:separate;border-spacing:3px;font-size:10px;min-width:100%;">
                <tr>
                  <td></td>
                  <td v-for="h in heatHours" :key="'h' + h" class="text-secondary text-center" style="min-width:22px;">{{ h % 3 === 0 ? hourLabel(h) : '' }}</td>
                </tr>
                <tr v-for="(day, di) in days" :key="day">
                  <td class="text-secondary pr-1" style="font-size:11px;">{{ day }}</td>
                  <td
                    v-for="h in heatHours"
                    :key="day + h"
                    :title="`${day} ${hourLabel(h)}: ${d.heatmap[di][h]} check-in(s)`"
                    :style="{ height: '20px', borderRadius: '4px', background: heatColor(d.heatmap[di][h]) }"
                  ></td>
                </tr>
              </table>
            </div>
            <p v-else class="text-secondary mb-0" style="font-size:12px;">No check-ins in this period.</p>
          </div>
        </div>

        <!-- Revenue by package -->
        <div class="col-12 col-lg-5">
          <div class="bg-white border h-100" :style="card">
            <div class="mb-3" style="font-size:12px;font-weight:600;text-transform:uppercase;letter-spacing:0.4px;color:rgba(253,71,25,0.8);">Income by package <i class="bi bi-question-circle text-secondary" style="font-size:11px;" title="New plans and renewals charged in the selected dates, incl. VAT."></i></div>
            <div v-for="p in d.packages" :key="p.name" class="mb-3">
              <div class="d-flex justify-content-between" style="font-size:13px;">
                <span class="text-dark">{{ p.name }} <span class="text-secondary">· {{ p.count }}</span></span>
                <strong class="text-dark">{{ money(p.amount) }}</strong>
              </div>
              <div class="progress mt-1" style="height:6px;">
                <div class="progress-bar bg-primary" :style="{ width: pct(p.amount) + '%' }"></div>
              </div>
            </div>
            <p v-if="!d.packages.length" class="text-secondary mb-0" style="font-size:12px;">No plans sold or renewed in this period.</p>
          </div>
        </div>

        <!-- Members who owe -->
        <div class="col-12 col-lg-4">
          <div class="bg-white border h-100" :style="card">
            <div class="d-flex align-items-center justify-content-between mb-2">
              <span style="font-size:12px;font-weight:600;text-transform:uppercase;letter-spacing:0.4px;color:rgba(253,71,25,0.8);">
                Members who owe
                <i class="bi bi-question-circle text-secondary" style="font-size:11px;" title="Everything charged on the membership (first plan + renewals) minus everything paid (payments + member wallet). Cancelled memberships excluded."></i>
              </span>
              <span v-if="d.owing.count" class="badge badge-danger">{{ d.owing.count }}</span>
            </div>
            <div v-if="d.owing.total > 0" class="text-danger mb-2" style="font-size:18px;font-weight:700;">{{ money(d.owing.total) }}</div>
            <a v-for="m in d.owing.members" :key="m.subscription_id" :href="'/subscriptions?subscription-id=' + m.subscription_id"
               class="d-flex align-items-center justify-content-between py-2 border-bottom text-decoration-none" style="gap:10px;">
              <div style="min-width:0;">
                <div class="text-dark text-truncate" style="font-size:13px;font-weight:600;">{{ m.name }}</div>
                <div class="text-secondary" style="font-size:12px;">{{ m.package }}<template v-if="m.status && m.status !== 'active'"> · {{ m.status.replace('_', ' ') }}</template></div>
              </div>
              <strong class="text-danger text-nowrap" style="font-size:13px;">{{ money(m.owed) }}</strong>
            </a>
            <p v-if="!d.owing.count" class="text-secondary mb-0" style="font-size:12px;">Every membership is fully paid.</p>
          </div>
        </div>

        <!-- Expiring soon -->
        <div class="col-12 col-lg-4">
          <div class="bg-white border h-100" :style="card">
            <div class="d-flex align-items-center justify-content-between mb-2">
              <span style="font-size:12px;font-weight:600;text-transform:uppercase;letter-spacing:0.4px;color:rgba(253,71,25,0.8);">Expiring in 7 days</span>
              <span class="badge badge-dark">{{ d.members.expiring_7d }}</span>
            </div>
            <div v-for="m in d.expiring" :key="m.subscription_id" class="d-flex align-items-center justify-content-between py-2 border-bottom" style="gap:10px;">
              <div style="min-width:0;">
                <div class="text-dark text-truncate" style="font-size:13px;font-weight:600;">{{ m.name }}</div>
                <div class="text-secondary" style="font-size:12px;">{{ m.package }} · {{ daysLeft(m.days_left) }}</div>
              </div>
              <button
                v-if="d.can_message && m.has_phone"
                class="btn btn-outline-primary btn-sm flex-shrink-0"
                :disabled="!!sent['r' + m.subscription_id] || busy === 'r' + m.subscription_id"
                @click="remind(m)"
              >
                <span v-if="busy === 'r' + m.subscription_id" class="spinner-border spinner-border-sm"></span>
                <template v-else>{{ sent['r' + m.subscription_id] ? 'Sent' : 'Remind' }}</template>
              </button>
            </div>
            <p v-if="!d.expiring.length" class="text-secondary mb-0" style="font-size:12px;">No plans expire in the next 7 days.</p>
          </div>
        </div>

        <!-- Lapsed regulars -->
        <div class="col-12 col-lg-4">
          <div class="bg-white border h-100" :style="card">
            <div class="d-flex align-items-center justify-content-between mb-2">
              <span style="font-size:12px;font-weight:600;text-transform:uppercase;letter-spacing:0.4px;color:rgba(253,71,25,0.8);">
                Regulars who stopped coming
                <i class="bi bi-question-circle text-secondary" style="font-size:11px;" title="Active members who used to come often but haven't checked in for a while (set under Subscriptions › Overview)."></i>
              </span>
              <a href="/subscriptions" class="text-primary" style="font-size:12px;">See all</a>
            </div>
            <div v-for="m in d.lapsed" :key="m.subscription_id" class="d-flex align-items-center justify-content-between py-2 border-bottom" style="gap:10px;">
              <div style="min-width:0;">
                <div class="text-dark text-truncate" style="font-size:13px;font-weight:600;">{{ m.name }}</div>
                <div class="text-secondary" style="font-size:12px;">{{ m.days_away }} days away · {{ m.visits }} visits before</div>
              </div>
              <button
                v-if="d.can_message && m.phone"
                class="btn btn-outline-primary btn-sm flex-shrink-0"
                :disabled="!!sent['n' + m.subscription_id] || busy === 'n' + m.subscription_id"
                @click="nudge(m)"
              >
                <span v-if="busy === 'n' + m.subscription_id" class="spinner-border spinner-border-sm"></span>
                <template v-else>{{ sent['n' + m.subscription_id] ? 'Sent' : 'Nudge' }}</template>
              </button>
            </div>
            <p v-if="!d.lapsed.length" class="text-secondary mb-0" style="font-size:12px;">Nobody's slipping away right now.</p>
          </div>
        </div>

      </div>
    </template>
  </div>
</template>

<script>
import { Chart, registerables } from 'chart.js';
Chart.register(...registerables);

/** Memberships block on /analytics/beta (companies with the subscriptions module). */
export default {
  name: 'MembershipBlock',

  props: {
    startDate: { type: String, required: true },
    endDate:   { type: String, required: true },
    // IncomeSources::trend payload - its Memberships series feeds the bars.
    trend:     { type: Object, default: null },
  },

  data() {
    return {
      d: null,
      loading: false,
      error: null,
      busy: null,
      sent: {},
      card: 'border-radius:14px;border-color:#e9ecef !important;padding:20px;',
      days: ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'],
      donutColors: ['#fd4719', '#343a40', '#ced4da'],
    };
  },

  computed: {
    healthStats() {
      const h = this.d.health;
      return [
        { label: 'Renewal rate', value: h.renewal_rate === null ? '—' : h.renewal_rate + '%', help: `Renewals ÷ (renewals + members lost). Lost = plan ended over ${h.lost_after_days} days ago and not renewed.` },
        { label: 'New members', value: h.new_members },
        { label: 'Renewals', value: h.renewals },
        { label: 'Members lost', value: h.lost, danger: h.lost > 0, help: `Plan ended in this period, over ${h.lost_after_days} days ago, with no renewal or new plan.` },
        { label: 'Check-ins', value: h.visits.toLocaleString('en-US') },
        { label: 'Avg visits / member', value: h.avg_visits },
      ];
    },

    donutSegments() {
      const m = this.d.members;
      return [
        { label: 'Active', value: Math.max(0, m.active - m.expiring_7d) },
        { label: 'Expiring in 7 days', value: m.expiring_7d },
        { label: 'Expired', value: m.expired },
      ];
    },

    heatMax() {
      return Math.max(1, ...this.d.heatmap.map(row => Math.max(...row)));
    },

    // Only the hours that ever have check-ins (e.g. 5am-10pm), not all 24.
    heatHours() {
      const used = [];
      for (let h = 0; h < 24; h++) if (this.d.heatmap.some(row => row[h] > 0)) used.push(h);
      if (!used.length) return [];
      const out = [];
      for (let h = used[0]; h <= used[used.length - 1]; h++) out.push(h);
      return out;
    },

    peak() {
      let best = null;
      this.d.heatmap.forEach((row, di) => row.forEach((n, h) => {
        if (n > 0 && (!best || n > best.n)) best = { n, di, h };
      }));
      return best ? `${this.days[best.di]} ${this.hourLabel(best.h)}` : null;
    },

    packageMax() {
      return Math.max(1, ...this.d.packages.map(p => p.amount));
    },
  },

  watch: {
    startDate() { this.load(); },
    endDate() { this.load(); },
    trend() { this.$nextTick(this.renderCharts); },
  },

  mounted() { this.load(); },

  beforeDestroy() {
    if (this.comboChart) this.comboChart.destroy();
    if (this.donutChart) this.donutChart.destroy();
  },

  methods: {
    async load() {
      this.loading = true;
      this.error = null;
      try {
        const { data } = await axios.get('/api/sales-analytics/memberships', {
          params: { start_date: this.startDate, end_date: this.endDate },
        });
        this.d = data;
        this.$nextTick(this.renderCharts);
      } catch (e) {
        this.error = e.response?.data?.message || 'Could not load membership numbers.';
      } finally {
        this.loading = false;
      }
    },

    renderCharts() {
      if (!this.d) return;
      this.renderCombo();
      this.renderDonut();
    },

    renderCombo() {
      if (this.comboChart) this.comboChart.destroy();
      if (!this.$refs.combo) return;
      const income = (this.trend && this.trend.series && this.trend.series.Memberships) || this.d.checkins.values.map(() => 0);

      this.comboChart = new Chart(this.$refs.combo.getContext('2d'), {
        data: {
          labels: this.d.checkins.labels,
          datasets: [
            { type: 'bar', label: 'Membership income', data: income, backgroundColor: '#fd4719', borderRadius: 4, maxBarThickness: 26, yAxisID: 'y', order: 2 },
            { type: 'line', label: 'Check-ins', data: this.d.checkins.values, borderColor: '#343a40', backgroundColor: '#343a40', borderWidth: 2, pointRadius: 2, tension: 0.35, yAxisID: 'y1', order: 1 },
          ],
        },
        options: {
          responsive: true,
          maintainAspectRatio: false,
          animation: { duration: 200 },
          interaction: { mode: 'index', intersect: false },
          plugins: {
            legend: { display: false },
            tooltip: {
              backgroundColor: '#1e293b', padding: 10, cornerRadius: 6,
              callbacks: { label: ctx => ctx.dataset.yAxisID === 'y' ? ` Income: ${this.money(ctx.parsed.y)}` : ` Check-ins: ${ctx.parsed.y}` },
            },
          },
          scales: {
            x: { grid: { display: false }, border: { display: false }, ticks: { font: { size: 11 }, color: '#94a3b8', maxRotation: 0, autoSkip: true } },
            y: { beginAtZero: true, position: 'left', grid: { color: 'rgba(0,0,0,0.04)' }, border: { display: false }, ticks: { font: { size: 11 }, color: '#94a3b8', callback: v => this.short(v) } },
            y1: { beginAtZero: true, position: 'right', grid: { display: false }, border: { display: false }, ticks: { font: { size: 11 }, color: '#94a3b8', precision: 0 } },
          },
        },
      });
    },

    renderDonut() {
      if (this.donutChart) this.donutChart.destroy();
      if (!this.$refs.donut) return;
      const values = this.donutSegments.map(s => s.value);
      const empty = values.every(v => !v);

      this.donutChart = new Chart(this.$refs.donut.getContext('2d'), {
        type: 'doughnut',
        data: {
          labels: this.donutSegments.map(s => s.label),
          datasets: [{ data: empty ? [1] : values, backgroundColor: empty ? ['#f1f3f5'] : this.donutColors, borderWidth: 0 }],
        },
        options: {
          responsive: true,
          maintainAspectRatio: false,
          cutout: '72%',
          animation: { duration: 200 },
          plugins: { legend: { display: false }, tooltip: { enabled: !empty } },
        },
      });
    },

    async remind(m) {
      await this.send('r' + m.subscription_id, `/api/subscriptions/${m.subscription_id}/remind-expiry`, `Reminder sent to ${m.name}.`);
    },

    async nudge(m) {
      await this.send('n' + m.subscription_id, `/api/subscriptions/inactive-members/${m.subscription_id}/nudge`, `Message sent to ${m.name}.`);
    },

    async send(key, url, okMessage) {
      this.busy = key;
      try {
        await axios.post(url);
        this.$set(this.sent, key, true);
        this.$toasts && this.$toasts.push({ type: 'success', message: okMessage });
      } catch (e) {
        this.$toasts && this.$toasts.push({ type: 'error', message: e.response?.data?.message || 'SMS could not be sent.' });
      } finally {
        this.busy = null;
      }
    },

    heatColor(n) {
      if (!n) return '#f1f3f5';
      return `rgba(253,71,25,${(0.15 + 0.85 * n / this.heatMax).toFixed(2)})`;
    },

    hourLabel(h) {
      const suffix = h < 12 ? 'am' : 'pm';
      return (h % 12 || 12) + suffix;
    },

    daysLeft(n) {
      return n <= 0 ? 'ends today' : n === 1 ? '1 day left' : `${n} days left`;
    },

    pct(amount) {
      return Math.max(2, Math.round(amount / this.packageMax * 100));
    },

    money(v) {
      return 'KES ' + Number(v || 0).toLocaleString('en-US', { maximumFractionDigits: 0 });
    },

    short(v) {
      if (v >= 1e6) return (v / 1e6).toFixed(1) + 'M';
      if (v >= 1e3) return (v / 1e3).toFixed(0) + 'K';
      return Math.round(v);
    },
  },
};
</script>
